import { useEffect, useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';

import useUserData from '../../../hooks/useUserData';
import useUserRole from '../../../hooks/useUserRole';
import { LOAN_FILE_STATUSES } from '../../../constants/formOptions';
import { USER_ROLES } from '../../../constants/main';
import BrokerService from '../../../services/BrokerService';
import BrokerTeamService from '../../../services/BrokerTeamService';
import { useTableFiltersStore } from '../../../store';
import UserService from '../../../services/UserService';

function areEqualArrays(arr1, arr2) {
  return isEqual(sortBy(arr1), sortBy(arr2));
}

function isOptionBelongToAnySelectedBroker({ optionId, options, brokerIds }) {
  if (!optionId || !options || !brokerIds) {
    return false;
  }

  const option = options.find((option) => option.value === optionId);

  if (!option) {
    return false;
  }

  const optionBrokerId = option.brokerId;

  return brokerIds.includes(optionBrokerId);
}

function removeOptionsForNonSelectedBrokers({
  filterKey,
  filters,
  selectedBrokers,
  filterName,
}) {
  const filtersStates = useTableFiltersStore.getState().filtersStates;
  const setFiltersStates = useTableFiltersStore.getState().setFiltersStates;

  const allOptions = filters.find(
    (filter) => filter.title === filterName
  )?.options;

  if (!allOptions) {
    return;
  }

  const selectedOptions = filtersStates[filterKey]?.[filterName];

  const filteredOptions = selectedOptions.filter((teamId) => {
    return isOptionBelongToAnySelectedBroker({
      optionId: teamId,
      options: allOptions.options,
      brokerIds: selectedBrokers,
    });
  });

  if (!areEqualArrays(filteredOptions, selectedOptions)) {
    setFiltersStates(filterKey, {
      ...filtersStates[filterKey],
      [filterName]: filteredOptions.map((team) => team.value),
    });
  }
}

export default function useFilters(filterKey) {
  const { user } = useUserData();
  const { isSuperAdmin } = useUserRole();

  const { data: brokers } = useQuery({
    queryKey: ['brokers'],
    queryFn: () => BrokerService.getBrokers(),
    enabled: isSuperAdmin,
  });

  const filtersStates = useTableFiltersStore((state) => state.filtersStates);
  const setFiltersStates = useTableFiltersStore(
    (state) => state.setFiltersStates
  );

  const brokerIds = useMemo(() => {
    if (isSuperAdmin) {
      return filtersStates[filterKey]?.Broker;
    } else if (user?.brokerId) {
      return [user.brokerId];
    } else {
      return [];
    }
  }, [isSuperAdmin, user.brokerId, filtersStates, filterKey]);

  const { data: brokerTeams } = useQuery({
    queryKey: ['brokerTeams', 'byBrokerIds', ...(brokerIds ?? [])],
    queryFn: () => BrokerTeamService.getBrokerTeams({ brokerIds }),
    enabled: !!user,
  });

  const { data: users } = useQuery({
    queryKey: ['loanOfficers', 'byBrokerIds', ...(brokerIds ?? [])],
    queryFn: () =>
      UserService.getUsers({
        brokerIds,
        role: USER_ROLES.loanOfficer,
      }),
    enabled: !!user?.brokerId,
  });

  const filters = useMemo(() => {
    const defaultFilters = [
      {
        title: 'Loan officer',
        subTitle: 'Select loan officer',
        options: users?.map((user) => ({
          label: user.name,
          value: user.id,
          brokerId: user.brokerId,
        })),
      },
      {
        title: 'Status',
        subTitle: 'Select status',
        options: LOAN_FILE_STATUSES.map((status) => {
          return {
            label: status.label,
            value: status.value,
            selected: ![
              'Originated',
              'Denied - Pre-Approval',
              'Denied',
              'Withdrawn',
              'Closed for Incompleteness',
              'Archived',
            ].includes(status.value),
          };
        }),
      },
      {
        title: 'Team',
        subTitle: 'Select team',
        options: brokerTeams?.map((team) => {
          return {
            label: team.name,
            value: team.id,
            selected: user.BrokerTeams?.some(
              (userTeam) => userTeam.id === team.id
            ),
            brokerId: team.brokerId,
          };
        }),
      },
    ];

    if (isSuperAdmin) {
      return [
        {
          title: 'Broker',
          subTitle: 'Select broker',
          options: brokers?.map((broker) => ({
            label: broker.name,
            value: broker.id,
          })),
        },
        ...defaultFilters,
      ];
    } else {
      return defaultFilters;
    }
  }, [users, brokerTeams, isSuperAdmin, user.BrokerTeams, brokers]);

  // Remove Team and Loan officers if they don't belong to the selected brokers (only for super admin)
  useEffect(() => {
    if (!isSuperAdmin) {
      return;
    }

    if (!brokerIds || !brokerIds.length) {
      return;
    }

    removeOptionsForNonSelectedBrokers({
      filterKey,
      filters,
      selectedBrokers: brokerIds,
      filterName: 'Team',
    });
    removeOptionsForNonSelectedBrokers({
      filterKey,
      filters,
      selectedBrokers: brokerIds,
      filterName: 'Loan officer',
    });
  }, [filterKey, filters, brokerIds, isSuperAdmin, setFiltersStates]);

  return filters;
}
